import { Hourglass } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';

interface PerformanceEmptyStateProps {
  trustworthyCount: number;
  onShowAllTrustworthy: () => void;
}

export function PerformanceEmptyState({ trustworthyCount, onShowAllTrustworthy }: PerformanceEmptyStateProps) {
  const { t } = useTranslation('performance');

  return (
    <EmptyState
      icon={Hourglass}
      title={t('emptyState.title', 'Daten werden noch gesammelt')}
      // Richtungsneutral, und zwar auch im Inline-Default: der Default ist der
      // Wortlaut, der bei fehlendem Locale-Schluessel erscheint. Die alte
      // Fassung versprach "statistisch bewiesene CTR-Verbesserungen", waehrend
      // die Liste zwei Zeilen darunter das ZWEISEITIGE Gate |z| >= 1,96
      // ausschreibt (gateSignificance): die Menge, auf die dieser Leerzustand
      // wartet, enthaelt also auch Rueckgaenge.
      description={t('emptyState.body', 'Sobald deine optimierten Produkte genug Performance-Daten sammeln, erscheinen hier statistisch belastbare CTR-Änderungen.')}
    >
      <div className="text-left max-w-md mx-auto bg-muted/30 rounded-md p-3 mb-4 text-xs">
        <div className="font-medium mb-1.5">{t('emptyState.gatesHeader', 'Voraussetzungen pro Produkt:')}</div>
        <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
          <li>{t('emptyState.gateUpload', 'Optimiert UND zu Google Merchant Center hochgeladen')}</li>
          <li>{t('emptyState.gateMinDays', '≥ 7 Tage seit Upload + 2 Tage Google-Latenz')}</li>
          <li>{t('emptyState.gateMinImpClk', '≥ 100 Impressionen UND ≥ 5 Klicks vor UND nach Upload')}</li>
          <li>{t('emptyState.gateCoverage', '≥ 70 % Tagesabdeckung beider Seiten')}</li>
          <li>{t('emptyState.gateSignificance', 'Pre/Post-CTR-Unterschied stark genug (|z| ≥ 1,96)')}</li>
        </ul>
      </div>

      {trustworthyCount > 0 && (
        <>
          {/* Numerischer `count` statt `{{n}}`, damit i18next die Pluralform
              waehlen kann — mit `n` stand hier bei genau einem Produkt
              "1 Produkte sind vergleichbar ... kein einziges" (Muster der
              Welle-3-Pluralfixes, Test: productCountPlurals.test.ts). */}
          <p className="text-xs text-muted-foreground mb-3">
            {t('emptyState.trustworthyHint', '{{count}} Produkte sind vergleichbar (haben genug Daten), aber kein einziges schlägt das statistische Rauschen.', { count: trustworthyCount })}
          </p>
          <button
            onClick={onShowAllTrustworthy}
            className="text-sm text-primary hover:underline"
          >
            {t('emptyState.showAllCta', 'Alle {{count}} vergleichbaren Produkte unten anzeigen →', { count: trustworthyCount })}
          </button>
        </>
      )}
    </EmptyState>
  );
}
